import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useNavigate } from "react-router";
import { Fingerprint } from "lucide-react";
import { useLocale, useTranslation } from "@pmo/i18n";
import { Button, GovBar, GovFooter, PageTransition, Tooltip, govNavItemClass, useDocumentIdentity } from "@pmo/ui";

const NAV = [
  { to: "/", key: "home" },
  { to: "/registry", key: "registry" },
  { to: "/consultations", key: "consultations" },
  { to: "/verify", key: "verify" },
  { to: "/about", key: "about" },
] as const;

/** The portal sections as the UAE Design System navigation row; the current one is underlined in AE Gold 900. */
function PortalNav() {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("common:nav.main")}>
      <ul className="flex w-max items-stretch gap-1">
        {NAV.map(({ to, key }) => (
          <li key={to} className="flex">
            <NavLink to={to} end={to === "/"} className={({ isActive }) => govNavItemClass(isActive)}>
              {t(`nav.${key}`)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** The public portal's page: the UAE Design System header, footer and white canvas around the routed page. */
export function PortalLayout() {
  const { t } = useTranslation();
  const { locale, toggle } = useLocale();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useDocumentIdentity(t("common:app.portalName"), t("common:app.portalDescription"));

  return (
    <div className="flex min-h-dvh flex-col">
      <ScrollRestoration />
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:start-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:shadow">
        {t("common:app.skipToContent")}
      </a>
      <GovBar
        ministryName={t("common:app.owner")}
        ministryNameSecondary={t("common:app.government")}
        platformName={t("common:app.portalName")}
        locale={locale}
        onToggleLocale={toggle}
        onHome={() => navigate("/")}
        emblem
        nav={<PortalNav />}
        right={
          <Tooltip content={t("uaePassSoon")}>
            <span>
              <Button variant="secondary" size="sm" disabled aria-describedby="uaepass-note">
                <Fingerprint aria-hidden />
                <span className="hidden sm:inline">{t("uaePass")}</span>
              </Button>
              <span id="uaepass-note" className="sr-only">{t("uaePassSoon")}</span>
            </span>
          </Tooltip>
        }
      />
      <main id="main" tabIndex={-1} className="@container/main mx-auto w-full max-w-7xl flex-1 px-4 py-8 outline-none sm:px-6 lg:px-8 lg:py-10">
        <PageTransition routeKey={pathname}>
          <Outlet />
        </PageTransition>
      </main>
      <GovFooter
        about={t("footer")}
        columns={[
          {
            title: t("common:app.portalName"),
            items: NAV.map(({ to, key }) => <Link key={to} to={to}>{t(`nav.${key}`)}</Link>),
          },
        ]}
      >
        {t("common:app.copyright", { year: new Date().getFullYear() })}
      </GovFooter>
    </div>
  );
}
